import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const maritalStatusKeys = {
  all: ['maritalStatuses'],
  lists: () => [...maritalStatusKeys.all, 'list'],
  details: () => [...maritalStatusKeys.all, 'detail'],
  detail: (id) => [...maritalStatusKeys.details(), id],
};

/**
 * Hook para obtener todos los estados civiles
 */
export const useMaritalStatuses = () => {
  return useQuery({
    queryKey: maritalStatusKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('marital_statuses');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un estado civil específico
 * @param {string} id - UUID del estado civil
 * @param {Object} options - Opciones adicionales para la query
 */
export const useMaritalStatus = (id, options = {}) => {
  return useQuery({
    queryKey: maritalStatusKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`marital_statuses/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo estado civil
 */
export const useCreateMaritalStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (maritalStatusData) => {
      const response = await apiClient.post('marital_statuses', maritalStatusData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maritalStatusKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating marital status:', error);
    },
  });
};

/**
 * Hook para actualizar un estado civil existente
 */
export const useUpdateMaritalStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (maritalStatusData) => {
      const cleanedData = { ...maritalStatusData };

      delete cleanedData.deleted_at;
      delete cleanedData.updated_at;
      delete cleanedData.created_at;
      delete cleanedData.is_active;
      delete cleanedData.modules;

      const response = await apiClient.put(`marital_statuses/${cleanedData.uuid}`, cleanedData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(maritalStatusKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: maritalStatusKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating marital status:', error);
    },
  });
};

/**
 * Hook para eliminar un estado civil
 */
export const useDeleteMaritalStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`marital_statuses/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: maritalStatusKeys.lists() });
      queryClient.removeQueries({ queryKey: maritalStatusKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting marital status:', error);
    },
  });
};
